import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import skillSetService, { SkillSet } from '../../services/skillSet.service';
import skillService from '../../services/skill.service';

const LEVEL_OPTIONS = [
  { value: '', label: 'Aucun' },
  { value: 'debutant', label: 'Débutant' },
  { value: 'intermediaire', label: 'Intermédiaire' },
  { value: 'avance', label: 'Avancé' },
  { value: 'expert', label: 'Expert' },
];

const COLOR_OPTIONS = [
  '#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2',
  '#0097a7', '#455a64', '#c2185b', '#5d4037', '#303f9f',
];

const LEVEL_LABELS: Record<string, string> = {
  debutant: 'Débutant',
  intermediaire: 'Intermédiaire',
  avance: 'Avancé',
  expert: 'Expert',
};

const SkillSets: React.FC = () => {
  const [skillSets, setSkillSets] = useState<SkillSet[]>([]);
  const [loading, setLoading] = useState(false);
  const initialLoadDone = useRef(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSet, setEditingSet] = useState<SkillSet | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState('');
  const [color, setColor] = useState('');
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]);

  // Skill picker
  const [allSkills, setAllSkills] = useState<any[]>([]);
  const [skillSearch, setSkillSearch] = useState('');
  const [loadingSkills, setLoadingSkills] = useState(false);

  const loadSkillSets = async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const data = await skillSetService.getAll();
      setSkillSets(data);
    } catch (error) {
      console.error('Error loading skill sets:', error);
      toast.error('Erreur lors du chargement des programmes');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  };

  const loadAllSkills = async () => {
    try {
      setLoadingSkills(true);
      const data = await skillService.getAll();
      setAllSkills(data);
    } catch (error) {
      console.error('Error loading skills:', error);
    } finally {
      setLoadingSkills(false);
    }
  };

  useEffect(() => {
    loadSkillSets();
  }, []);

  const openCreateDialog = () => {
    setEditingSet(null);
    setTitle('');
    setDescription('');
    setLevel('');
    setColor('');
    setSelectedSkillIds([]);
    setSkillSearch('');
    setDialogOpen(true);
    loadAllSkills();
  };

  const openEditDialog = (set: SkillSet) => {
    setEditingSet(set);
    setTitle(set.title);
    setDescription(set.description);
    setLevel(set.level);
    setColor(set.color);
    setSelectedSkillIds(set.skills || []);
    setSkillSearch('');
    setDialogOpen(true);
    loadAllSkills();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Le titre est requis');
      return;
    }

    try {
      const data = {
        title: title.trim(),
        description,
        level,
        color,
        skills: selectedSkillIds,
      };

      if (editingSet) {
        await skillSetService.update(editingSet.id, data);
        toast.success('Programme mis à jour');
      } else {
        await skillSetService.create(data);
        toast.success('Programme créé');
      }
      setDialogOpen(false);
      loadSkillSets();
    } catch (error) {
      console.error('Error saving skill set:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce programme ?')) return;
    try {
      await skillSetService.delete(id);
      toast.success('Programme supprimé');
      loadSkillSets();
    } catch (error) {
      console.error('Error deleting skill set:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (id: number) => {
    try {
      await skillSetService.duplicate(id);
      toast.success('Programme dupliqué');
      loadSkillSets();
    } catch (error) {
      console.error('Error duplicating skill set:', error);
      toast.error('Erreur lors de la duplication');
    }
  };

  const toggleSkill = (skillId: number) => {
    setSelectedSkillIds(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const filteredSkills = skillSearch.trim()
    ? allSkills.filter(s => s.name.toLowerCase().includes(skillSearch.trim().toLowerCase()))
    : allSkills;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Programmes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          Nouveau programme
        </Button>
      </Box>

      {skillSets.length === 0 ? (
        <Typography color="text.secondary" fontStyle="italic">
          Aucun programme. Créez un programme pour regrouper des savoir-faire.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {skillSets.map((set) => (
            <Grid item xs={12} sm={6} md={4} key={set.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: set.color ? `4px solid ${set.color}` : '4px solid transparent',
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="h6" sx={{ flex: 1, fontSize: '1rem' }}>
                      {set.title}
                    </Typography>
                    {set.level && (
                      <Chip
                        label={LEVEL_LABELS[set.level] || set.level}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  {set.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {set.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {set.skills_count} savoir-faire
                  </Typography>
                  {set.skills_details && set.skills_details.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {set.skills_details.slice(0, 8).map((skill) => (
                        <Chip key={skill.id} label={skill.name} size="small" variant="outlined" />
                      ))}
                      {set.skills_details.length > 8 && (
                        <Chip label={`+${set.skills_details.length - 8}`} size="small" />
                      )}
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <IconButton size="small" onClick={() => handleDuplicate(set.id)} title="Dupliquer">
                    <DuplicateIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => openEditDialog(set)} title="Modifier">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(set.id)} title="Supprimer" sx={{ color: 'error.main' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSet ? 'Modifier le programme' : 'Nouveau programme'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Titre"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            margin="dense"
            label="Description (optionnel)"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Niveau"
            fullWidth
            variant="outlined"
            select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            {LEVEL_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="body2" gutterBottom>Couleur</Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {COLOR_OPTIONS.map((c) => (
                <Box
                  key={c}
                  onClick={() => setColor(color === c ? '' : c)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: c,
                    cursor: 'pointer',
                    border: color === c ? '3px solid' : '2px solid transparent',
                    borderColor: color === c ? 'text.primary' : 'transparent',
                  }}
                />
              ))}
            </Box>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            Savoir-faire ({selectedSkillIds.length} sélectionnés)
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Rechercher un savoir-faire..."
            value={skillSearch}
            onChange={(e) => setSkillSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ maxHeight: 250, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 0.5 }}>
            {loadingSkills ? (
              <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
            ) : filteredSkills.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Aucun savoir-faire trouvé
              </Typography>
            ) : (
              <List dense disablePadding>
                {filteredSkills.map((skill) => (
                  <ListItem
                    key={skill.id}
                    dense
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => toggleSkill(skill.id)}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedSkillIds.includes(skill.id)}
                      tabIndex={-1}
                      disableRipple
                      sx={{ p: 0.25, mr: 1 }}
                    />
                    <ListItemText primary={skill.name} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={!title.trim()}>
            {editingSet ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SkillSets;
